import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, FloorPlan, FloorPlanList, StateScreen } from '@cowoo/shared/ui-web';
import { STALE_TIME, qk, todayBerlin, type DeskState } from '@cowoo/shared';
import { api } from '../../lib/api';
import { useCheckoutStore } from '../../lib/session';

/**
 * `/platz` — pick the day and the desk (09-web-member § 5).
 *
 * The plan and the availability are two calls on purpose: the plan is cached
 * until its `version` changes, availability is refetched per day and patched by
 * SSE `desk` events.
 */
export function DeskPicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setItems = useCheckoutStore((state) => state.setItems);

  const [day, setDay] = useState(todayBerlin());
  const problem = params.get('problem');
  const knownProblem = problem === 'desk_taken' || problem === 'hold_expired' ? problem : null;
  const [selected, setSelected] = useState<string | null>(null);

  const locations = useQuery({
    queryKey: qk.locations(),
    staleTime: STALE_TIME.locations,
    queryFn: async () => {
      const { data, error } = await api.GET('/locations');
      if (error || !data) throw error ?? new Error('locations unavailable');
      return data;
    },
  });

  const locationId = locations.data?.items.find((location) => location.canBookNow)?.id ?? null;

  const plan = useQuery({
    queryKey: locationId ? qk.plan(locationId) : ['plan', 'none'],
    enabled: locationId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/plan', {
        params: { path: { id: locationId as string } },
      });
      if (error || !data) throw error ?? new Error('plan unavailable');
      return data;
    },
  });

  const availability = useQuery({
    queryKey: locationId ? qk.availability(locationId, day) : ['availability', 'none'],
    enabled: locationId !== null,
    staleTime: STALE_TIME.availability,
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/availability', {
        params: { path: { id: locationId as string }, query: { day } },
      });
      if (error || !data) throw error ?? new Error('availability unavailable');
      return data;
    },
  });

  const desks = useMemo(
    () =>
      (plan.data?.plans ?? [])
        .flatMap((entry) => entry.placements)
        .filter((placement) => placement.kind === 'desk' && placement.deskType === 'flex'),
    [plan.data],
  );

  const stateOf = (resourceId: string): DeskState => {
    if (selected === resourceId) return 'selected';
    const found =
      'desks' in (availability.data ?? {})
        ? (availability.data as { desks: { resourceId: string; state: string }[] }).desks.find(
            (desk) => desk.resourceId === resourceId,
          )
        : undefined;
    if (!found) return 'taken';
    return found.state === 'free' ? 'free' : found.state === 'mine' ? 'mine' : 'taken';
  };

  if (locations.isLoading) return <StateScreen title={t('common.loading')} />;
  if (!locationId) {
    // BR-BOOK-10: outside the priority window nobody can book a closed house.
    return <StateScreen title={t('errors.location_not_open')} />;
  }

  function proceed(): void {
    if (!selected || !locationId) return;
    const product = params.get('product');
    setItems(
      product === 'private_desk'
        ? [{ type: 'contract', productKey: 'private_desk', resourceId: selected }]
        : product === 'flex_desk'
          ? [{ type: 'contract', productKey: 'flex_desk' }]
          : [{ type: 'day_ticket', locationId, day, resourceId: selected }],
    );
    navigate('/angaben');
  }

  return (
    <main className="max-w-content mx-auto px-5 py-12">
      <h1 className="font-heading text-ink mb-6 text-3xl">{t('member.desk.title')}</h1>

      <label className="font-body text-muted mb-6 block text-sm">
        {t('member.desk.day')}
        <input
          type="date"
          value={day}
          min={todayBerlin()}
          onChange={(event) => setDay(event.target.value)}
          className="border-line-strong rounded-input h-field font-body text-ink ml-3 border px-4"
        />
      </label>

      {/* § 8.3 — sent back here by a refused order: say why, then let them pick again. */}
      {knownProblem ? <Banner tone="danger">{t(`errors.${knownProblem}`)}</Banner> : null}
      {availability.isError ? <Banner tone="danger">{t('errors.unknown')}</Banner> : null}

      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <FloorPlan objects={desks} stateOf={stateOf} onSelect={setSelected} t={t} />
        {/* The list is not a fallback: it is the keyboard and screen-reader path. */}
        <FloorPlanList objects={desks} stateOf={stateOf} onSelect={setSelected} t={t} />
      </div>

      <div className="mt-8">
        <Button onClick={proceed} disabled={!selected}>
          {t('common.continue')}
        </Button>
      </div>
    </main>
  );
}
